import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  inquiriesApi,
  type InquiryEvent,
  type InquiryStatus,
} from '@/lib/api/inquiries.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';

const STATUS_OPTIONS: InquiryStatus[] = [
  'new',
  'contacted',
  'quoted',
  'follow_up',
  'lost',
  'cancelled',
];

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  quoted: 'Quoted',
  follow_up: 'Follow-up',
  converted: 'Converted',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

function formatInquiryStatus(status: string) {
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

function inquiryEventLabel(event: InquiryEvent) {
  const { eventType, payload } = event;

  switch (eventType) {
    case 'inquiry.created':
      return 'Inquiry created';
    case 'inquiry.updated':
      return 'Inquiry updated';
    case 'inquiry.status_changed':
      return `Status changed: ${formatInquiryStatus(String(payload.from ?? ''))} → ${formatInquiryStatus(String(payload.to ?? ''))}`;
    case 'inquiry.follow_up': {
      const method = String(payload.method ?? '');
      if (method === 'sms') return 'Follow-up sent via SMS';
      if (method === 'email') return 'Follow-up sent via email';
      if (method === 'phone') return 'Follow-up call logged';
      return 'Follow-up logged';
    }
    case 'inquiry.converted':
      return payload.ticketNumber
        ? `Converted to ticket ${payload.ticketNumber}`
        : 'Converted to ticket';
    default:
      return eventType.replace(/^inquiry\./, '').replace(/_/g, ' ');
  }
}

function inquiryEventDetail(event: InquiryEvent) {
  const { eventType, payload } = event;

  if (eventType === 'inquiry.status_changed' && payload.reason) {
    return String(payload.reason);
  }
  if (eventType === 'inquiry.follow_up' && payload.message) {
    return String(payload.message);
  }
  return null;
}

export function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [followUpMessage, setFollowUpMessage] = useState('');

  const detailQuery = useQuery({
    queryKey: ['inquiry', id],
    queryFn: () => inquiriesApi.get(id!),
    enabled: Boolean(id),
  });

  const inquiry = detailQuery.data?.data;

  const convertMut = useMutation({
    mutationFn: () => inquiriesApi.convert(id!),
    onSuccess: (res) => {
      toastSuccess(`Converted to ticket ${res.data.ticket.ticketNumber}`);
      queryClient.invalidateQueries({ queryKey: ['inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      navigate(`/tickets/${res.data.ticket.id}`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const statusMut = useMutation({
    mutationFn: (status: InquiryStatus) => inquiriesApi.setStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry', id] });
      queryClient.invalidateQueries({ queryKey: ['inquiries-stats'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const followUpMut = useMutation({
    mutationFn: (method: 'sms' | 'email' | 'phone') =>
      inquiriesApi.followUp(id!, { method, message: followUpMessage.trim() || undefined }),
    onSuccess: () => {
      toastSuccess('Follow-up logged');
      setFollowUpMessage('');
      queryClient.invalidateQueries({ queryKey: ['inquiry', id] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (detailQuery.isLoading) {
    return <p className="text-slate-500">Loading…</p>;
  }

  if (!inquiry) {
    return <p className="text-slate-500">Inquiry not found</p>;
  }

  const canConvert = !['converted', 'cancelled'].includes(inquiry.status);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/inquiries" className="text-sm font-semibold text-primary">
            ← Inquiries
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-slate-950">{inquiry.inquiryNumber}</h1>
          <p className="text-sm capitalize text-slate-600">{inquiry.status.replace('_', ' ')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canConvert ? (
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={convertMut.isPending}
              onClick={() => convertMut.mutate()}
            >
              {convertMut.isPending ? 'Converting…' : 'Convert to Ticket →'}
            </button>
          ) : null}
          {inquiry.convertedTicket ? (
            <Link
              to={`/tickets/${inquiry.convertedTicket.id}`}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
            >
              View {inquiry.convertedTicket.ticketNumber}
            </Link>
          ) : null}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="font-bold text-slate-950">Details</h2>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Customer</dt>
              <dd className="font-medium">{inquiry.customer.name ?? '—'}</dd>
              <dd className="text-slate-600">{inquiry.customer.phone}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Device</dt>
              <dd className="font-medium">
                {[inquiry.deviceMake, inquiry.deviceModel].filter(Boolean).join(' ') || '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-slate-500">Issue</dt>
              <dd>{inquiry.issueDescription ?? '—'}</dd>
            </div>
          </dl>

          <div>
            <h3 className="mb-2 font-semibold text-slate-950">Services quoted</h3>
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {(inquiry.servicesQuoted ?? []).map((s, i) => (
                <li key={i} className="flex justify-between px-4 py-2 text-sm">
                  <span>{s.serviceName}</span>
                  <span className="font-semibold">{formatCurrency(s.price)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-right font-bold">{formatCurrency(inquiry.quotedTotal)}</p>
          </div>

          {inquiry.notes ? (
            <div>
              <h3 className="font-semibold text-slate-950">Notes</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{inquiry.notes}</p>
            </div>
          ) : null}
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-semibold text-slate-950">Status</h3>
            <select
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={inquiry.status}
              disabled={statusMut.isPending || inquiry.status === 'converted'}
              onChange={(e) => statusMut.mutate(e.target.value as InquiryStatus)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace('_', ' ')}
                </option>
              ))}
              {inquiry.status === 'converted' ? (
                <option value="converted">converted</option>
              ) : null}
            </select>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-semibold text-slate-950">Follow-up</h3>
            <textarea
              className="mt-2 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="Optional custom message..."
              value={followUpMessage}
              onChange={(e) => setFollowUpMessage(e.target.value)}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold disabled:opacity-50"
                disabled={followUpMut.isPending}
                onClick={() => followUpMut.mutate('sms')}
              >
                SMS
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold disabled:opacity-50"
                disabled={followUpMut.isPending}
                onClick={() => followUpMut.mutate('email')}
              >
                Email
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold disabled:opacity-50"
                disabled={followUpMut.isPending}
                onClick={() => followUpMut.mutate('phone')}
              >
                Log call
              </button>
            </div>
            {inquiry.followUpAt ? (
              <p className="mt-2 text-xs text-slate-500">
                Next: {formatDateTime(inquiry.followUpAt)}
              </p>
            ) : null}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-semibold text-slate-950">Activity</h3>
            <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-xs">
              {(inquiry.events ?? []).map((ev) => {
                const detail = inquiryEventDetail(ev);
                return (
                  <li key={ev.id} className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="font-semibold text-slate-800">{inquiryEventLabel(ev)}</p>
                    {detail ? (
                      <p className="mt-0.5 whitespace-pre-wrap text-slate-600">{detail}</p>
                    ) : null}
                    <p className="mt-0.5 text-slate-500">{formatDateTime(ev.createdAt)}</p>
                  </li>
                );
              })}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
